import { useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../auth/auth-store';
import { searchChat, type ChatSearchResult } from '../api/console';
import { clearAppBadge } from '../pwa/register-sw';

interface Props {
  nav?: React.ReactNode;
}

function fmtAt(iso: string): string {
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// 全件チャット検索（ヘッダー・ログアウト横）。ヒットをクリックすると該当スレッドを開く。
function ChatSearch() {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  // 入力のデバウンス（250ms）。サーバ全件検索を叩きすぎない。
  useEffect(() => {
    const t = setTimeout(() => setQ(raw.trim()), 250);
    return () => clearTimeout(t);
  }, [raw]);

  // 外側クリックで結果を閉じる。
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, []);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['chat-search', q],
    queryFn: () => searchChat(q),
    enabled: q.length >= 1,
  });

  const goto = (r: ChatSearchResult) => {
    setOpen(false);
    const params = new URLSearchParams({ open: r.conversationId, pj: String(r.projectId) });
    if (r.teamId != null) params.set('team', String(r.teamId));
    navigate(`/?${params.toString()}`);
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        value={raw}
        onChange={(e) => { setRaw(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="🔍 チャットを検索（全件）"
        style={{
          width: 220,
          padding: '6px 10px',
          fontSize: 13,
          border: '1px solid #cbd5e1',
          borderRadius: 8,
          background: '#fff',
        }}
      />
      {open && q.length >= 1 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            width: 380,
            maxHeight: 420,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            zIndex: 1000,
            padding: 6,
          }}
        >
          {isFetching && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 13, color: '#64748b' }}>検索中…</div>
          )}
          {!isFetching && results.length === 0 && (
            <div style={{ padding: 12, fontSize: 13, color: '#64748b' }}>ヒットなし</div>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.conversationId}:${i}`}
              onClick={() => goto(r)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 'none',
                background: 'transparent',
                padding: '8px 10px',
                borderRadius: 8,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <div style={{ fontSize: 11, color: '#7c3aed', marginBottom: 2 }}>
                {r.projectName}
                {r.teamName ? ` / ${r.teamIcon ? `${r.teamIcon} ` : ''}${r.teamName}` : ''} · {fmtAt(r.createdAt)}
              </div>
              <div style={{ fontSize: 13, color: '#1d1c1d', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {r.role === 'user' ? '🧑 ' : r.role === 'ai' ? '🤖 ' : '⚙️ '}
                {r.snippet}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppLayout({ nav }: Props) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clear = useAuthStore((s) => s.clear);
  const [swUpdated, setSwUpdated] = useState(false);

  // アプリが前面に来たときにホーム画面バッジをクリアする
  useEffect(() => {
    clearAppBadge();
    const onVisibility = () => { if (document.visibilityState === 'visible') clearAppBadge(); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // 新しい SW が有効化された（= 新バージョンデプロイ）ときにバナーを出す
  useEffect(() => {
    const onUpdated = () => setSwUpdated(true);
    window.addEventListener('konecco-sw-updated', onUpdated);
    return () => window.removeEventListener('konecco-sw-updated', onUpdated);
  }, []);

  const onLogout = () => {
    clear();
    navigate('/login', { replace: true });
  };

  return (
    <>
      {swUpdated && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: '#7c3aed',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: '10px 20px',
          fontSize: 14,
        }}>
          <span>新しいバージョンの Konecco があります</span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: '#fff',
              color: '#7c3aed',
              border: 'none',
              borderRadius: 6,
              padding: '4px 14px',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            今すぐ更新
          </button>
          <button
            type="button"
            onClick={() => setSwUpdated(false)}
            style={{
              background: 'transparent',
              color: 'rgba(255,255,255,0.7)',
              border: 'none',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1,
              padding: '0 4px',
            }}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>
      )}
      <header className="k-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <h1>Konecco</h1>
          <span className="k-mobile-hidden">{nav}</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span className="k-mobile-hidden"><ChatSearch /></span>
          {user?.email && <span className="k-muted">{user.email}</span>}
          <button type="button" onClick={onLogout} className="k-mobile-hidden">
            ログアウト
          </button>
        </div>
      </header>
      <main className="k-main-full">
        <Outlet />
      </main>
    </>
  );
}
